import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type RefObject,
} from "react";
import {
  Alert,
  Avatar,
  Badge,
  Box,
  Button,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import MinimizeRoundedIcon from "@mui/icons-material/MinimizeRounded";
import { useAuth } from "@bw/shared/hooks";
import {
  authorLabel,
  authorInitials,
  MISSING,
  relativeTimeLabel,
  messageTimeLabel,
} from "@bw/shared/components";
import type {
  ConversationResponse,
  ConversationParticipantSummary,
  MessageResponse,
  PlayerCard as PlayerCardData,
} from "@bw/shared/types/generated";
import { useConversations } from "../hooks/useConversations";
import { useMessages } from "../hooks/useMessages";
import { useObservedReadBoundary } from "../hooks/useObservedReadBoundary";
import { usePlayerSearch } from "../hooks/usePlayerSearch";
import { useCreateConversation } from "../hooks/useCreateConversation";
import { useIsDesktop } from "../hooks/useIsDesktop";
import { useScrollToConfirmedMessage } from "../hooks/useScrollToConfirmedMessage";
import { useMessagingDock } from "../state/messagingDockStore";
import { linkifyContent } from "./MessagesLayout";
import { SharedPostEmbed } from "./SharedPostEmbed";
import { conversationPreview } from "../lib/conversationPreview";
import { MessageDeleteAction } from "./MessageDeleteAction";
import { MessageAttachments } from "./MessageAttachments";
import { MessageComposer } from "./MessageComposer";
import { GuardianAwareError } from "../../../common/guardian/GuardianAwareError";

/**
 * MessagingDock — a LinkedIn-style DOCKED messaging widget: a fixed bottom-right
 * panel that is a PURE PRESENTATION shell over the EXISTING DM system. It invents
 * no server state — every conversation row, message bubble, and unread count is a
 * DM React-Query hook's payload rendered VERBATIM (useConversations / useMessages /
 * useSendMessage / useMarkRead / useCreateConversation / usePlayerSearch). The only
 * new state is the dock's own UI shape (expanded? which panes open/minimized?),
 * which lives in the `messagingDockStore` Zustand singleton.
 *
 * Coexists with the AppBar's Messages action: both sum the SAME `unread_count`
 * from `useConversations`, so the icon-badge and the dock-badge
 * always agree (consistent, not competing). The dock is the desktop rich surface;
 * the AppBar button remains the mobile → /community/messages path.
 *
 * Gates (both fail-closed to rendering NOTHING):
 *   - DESKTOP-ONLY: below 64rem the dock is suppressed (`useIsDesktop`) — mobile
 *     messaging stays the AppBar button → the full-page /community/messages surface.
 *   - AUTH-GATED: a logged-out viewer renders nothing (no `me`, no `/me`-scoped
 *     conversation fetch).
 *
 * Honesty discipline: the summed-unread badge shows the REAL sum and only when the
 * list has SUCCESSFULLY loaded (no badge while loading / on error / when the sum is
 * 0 — never a fabricated "0"). Names are uid-safe (`authorLabel` → em-dash, never a
 * raw uid). Sends are NON-optimistic (the sent line arrives via the messages
 * refetch, never a phantom row); a failed send/create surfaces honestly.
 */
export function MessagingDock() {
  // Both hooks run unconditionally (stable order); the gates only change what we
  // render. `me` is the auth uid — the counterpart split + the /me-scoped fetch.
  const isDesktop = useIsDesktop();
  const { user } = useAuth();
  const me = user?.uid;
  const ownerId = useMessagingDock((state) => state.ownerId);
  const bindOwner = useMessagingDock((state) => state.bindOwner);

  useEffect(() => {
    bindOwner(me ?? null);
  }, [bindOwner, me]);

  // Fail closed: no dock on mobile / for a signed-out viewer. Returning before the
  // inner component mounts means NO conversation fetch fires in either case. An
  // account switch also renders nothing until the singleton has discarded the
  // prior account's conversation IDs.
  if (!isDesktop || !me || ownerId !== me) return null;

  return <MessagingDockInner me={me} />;
}

// =============================================================================
// The mounted dock (desktop + signed-in) — the fixed bottom-right container
// =============================================================================

function MessagingDockInner({ me }: { me: string }) {
  const expanded = useMessagingDock((s) => s.expanded);
  const panes = useMessagingDock((s) => s.panes);
  const canShowThreePanes = useMediaQuery("(min-width:84rem)", {
    noSsr: true,
  });
  const canShowTwoPanes = useMediaQuery("(min-width:68rem)", {
    noSsr: true,
  });
  const visiblePaneLimit = canShowThreePanes ? 3 : canShowTwoPanes ? 2 : 1;
  const openPanes = panes.filter((pane) => !pane.minimized);
  const selectedConversationIds = new Set(
    openPanes.slice(-visiblePaneLimit).map((pane) => pane.conversationId),
  );
  const remainingSlots = visiblePaneLimit - selectedConversationIds.size;
  if (remainingSlots > 0) {
    for (const pane of panes
      .filter((candidate) => candidate.minimized)
      .slice(-remainingSlots)) {
      selectedConversationIds.add(pane.conversationId);
    }
  }
  const visiblePanes = panes.filter((pane) =>
    selectedConversationIds.has(pane.conversationId),
  );

  const conversationsQ = useConversations();
  const conversations: ConversationResponse[] = conversationsQ.data ?? [];

  // Resolve each open pane's conversation from the inbox cache (there is no
  // getConversation(id) endpoint) so a pane can label its counterpart without a
  // second fetch. A cold pane (id not cached) degrades to the neutral title.
  const byId = useMemo(
    () => new Map(conversations.map((c) => [c.conversation_id, c])),
    [conversations],
  );

  // The summed-unread badge uses the SAME canonical conversation data as the
  // AppBar. Last-good data remains visible across a failed background refetch;
  // a cold load/error has no data and therefore shows no fabricated count.
  const unreadSum = conversations.reduce(
    (total, c) => total + (c.unread_count ?? 0),
    0,
  );
  const showBadge = conversationsQ.data !== undefined && unreadSum > 0;

  return (
    <Stack
      direction="row"
      spacing={1}
      data-testid="messaging-dock"
      sx={{
        position: "fixed",
        right: 24,
        bottom: 0,
        zIndex: (theme) => theme.zIndex.appBar + 1,
        alignItems: "flex-end",
        pointerEvents: "none",
      }}
    >
      {/* Open chat panes, docked to the LEFT of the bar, stacked oldest→newest. */}
      <Stack direction="row" spacing={1} sx={{ alignItems: "flex-end" }}>
        {visiblePanes.map((pane) => (
          <ChatPane
            key={pane.conversationId}
            conversationId={pane.conversationId}
            minimized={pane.minimized}
            conversation={byId.get(pane.conversationId)}
            me={me}
          />
        ))}
      </Stack>

      {/* The rightmost column: the always-present bar + the expandable list. */}
      <DockColumn
        me={me}
        conversationsQ={conversationsQ}
        conversations={conversations}
        expanded={expanded}
        showBadge={showBadge}
        unreadSum={unreadSum}
      />
    </Stack>
  );
}

// =============================================================================
// The bar + expandable conversation-list panel (rightmost column)
// =============================================================================

function DockColumn({
  me,
  conversationsQ,
  conversations,
  expanded,
  showBadge,
  unreadSum,
}: {
  me: string;
  conversationsQ: ReturnType<typeof useConversations>;
  conversations: ConversationResponse[];
  expanded: boolean;
  showBadge: boolean;
  unreadSum: number;
}) {
  const toggleDock = useMessagingDock((s) => s.toggleDock);
  const openConversation = useMessagingDock((s) => s.openConversation);

  // The compose (new-message) view is a LOCAL panel state — it replaces the list
  // body while the player searches. Collapsing the dock resets it.
  const [composing, setComposing] = useState(false);
  useEffect(() => {
    if (!expanded) setComposing(false);
  }, [expanded]);

  // Fold the unread total into the toggle's accessible name (the visual badge is
  // decorative / aria-hidden) so a screen-reader hears "Messaging, 5 unread".
  const barLabel = showBadge ? `Messaging, ${unreadSum} unread` : "Messaging";

  return (
    <Paper
      variant="outlined"
      sx={{
        width: 336,
        overflow: "hidden",
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        boxShadow: 8,
        pointerEvents: "auto",
      }}
    >
      {expanded ? (
        <Box sx={{ height: 420, overflow: "auto" }}>
          {composing ? (
            <DockCompose
              me={me}
              onPicked={(conversationId) => {
                setComposing(false);
                openConversation(conversationId);
              }}
            />
          ) : (
            <DockConversationList
              conversationsQ={conversationsQ}
              conversations={conversations}
              me={me}
              onOpen={openConversation}
            />
          )}
        </Box>
      ) : null}

      <Stack
        direction="row"
        sx={{
          alignItems: "center",
          borderTop: expanded ? 1 : 0,
          borderColor: "divider",
        }}
      >
        <Button
          type="button"
          onClick={toggleDock}
          aria-expanded={expanded}
          aria-label={barLabel}
          color="inherit"
          fullWidth
          sx={{ justifyContent: "flex-start", px: 2, py: 1.25 }}
          endIcon={
            expanded ? <ExpandMoreRoundedIcon /> : <ExpandLessRoundedIcon />
          }
        >
          <Typography
            component="span"
            sx={{ fontWeight: 800, flex: 1, textAlign: "left" }}
            aria-hidden
          >
            Messaging
          </Typography>
          {showBadge ? (
            <Chip
              size="small"
              color="primary"
              data-testid="messaging-dock-unread"
              label={unreadSum > 99 ? "99+" : unreadSum}
              aria-hidden="true"
            />
          ) : null}
        </Button>

        {expanded ? (
          <IconButton
            type="button"
            onClick={() => setComposing((c) => !c)}
            aria-pressed={composing}
            aria-label={composing ? "Cancel new message" : "New message"}
            sx={{ mr: 1 }}
          >
            {composing ? <CloseRoundedIcon /> : <EditOutlinedIcon />}
          </IconButton>
        ) : null}
      </Stack>
    </Paper>
  );
}

// =============================================================================
// The scrollable conversation list (expanded panel body, default view)
// =============================================================================

function DockConversationList({
  conversationsQ,
  conversations,
  me,
  onOpen,
}: {
  conversationsQ: ReturnType<typeof useConversations>;
  conversations: ConversationResponse[];
  me: string;
  onOpen: (conversationId: string) => void;
}) {
  // Alert ONLY on a first-load failure (error with NO cached data). The inbox
  // polls; a transient background-refetch rejection keeps the cached list up.
  if (conversationsQ.isError && !conversationsQ.data) {
    return (
      <Alert severity="error" role="alert" sx={{ m: 1.5 }}>
        Couldn&apos;t load your messages. Please try again.
      </Alert>
    );
  }
  if (conversationsQ.isLoading) {
    return (
      <Typography color="text.secondary" role="status" sx={{ p: 2 }}>
        Loading conversations…
      </Typography>
    );
  }
  if (conversations.length === 0) {
    return (
      <Typography color="text.secondary" sx={{ p: 2 }}>
        No conversations yet
      </Typography>
    );
  }
  return (
    <List disablePadding>
      {conversations.map((conversation) => (
        <DockConversationRow
          key={conversation.conversation_id}
          conversation={conversation}
          me={me}
          onOpen={onOpen}
        />
      ))}
    </List>
  );
}

function DockConversationRow({
  conversation,
  me,
  onOpen,
}: {
  conversation: ConversationResponse;
  me: string;
  onOpen: (conversationId: string) => void;
}) {
  const cp = counterpartOf(conversation, me);
  // Same pure derivation as MessagesLayout's inbox row: real text verbatim,
  // "Shared a post" for a shared-post-only last message, else "No messages yet".
  const preview = conversationPreview(conversation.last_message);
  const time = relativeTimeLabel(conversation.last_message_at);
  const showUnread = conversation.unread_count > 0;

  return (
    <ListItem disablePadding>
      <ListItemButton
        onClick={() => onOpen(conversation.conversation_id)}
        sx={{ py: 1.25 }}
      >
        <ListItemAvatar>
          <DockAvatar url={cp.avatarUrl} displayName={cp.displayName} />
        </ListItemAvatar>
        <ListItemText
          primary={
            <Stack
              direction="row"
              spacing={1}
              sx={{ justifyContent: "space-between", alignItems: "baseline" }}
            >
              <Typography
                component="span"
                sx={{ fontWeight: showUnread ? 800 : 600 }}
              >
                {cp.label}
              </Typography>
              {time ? (
                <Typography
                  component="span"
                  variant="caption"
                  color="text.secondary"
                >
                  {time}
                </Typography>
              ) : null}
            </Stack>
          }
          secondary={
            <Typography
              component="span"
              variant="body2"
              color="text.secondary"
              noWrap
            >
              {preview ? preview : "No messages yet"}
            </Typography>
          }
        />
        {showUnread ? (
          <Badge
            color="primary"
            variant="dot"
            aria-label={`${conversation.unread_count} unread`}
          >
            <Box sx={{ width: 8 }} />
          </Badge>
        ) : null}
      </ListItemButton>
    </ListItem>
  );
}

// =============================================================================
// A chat pane (one open conversation) — header + thread + composer
// =============================================================================

function ChatPane({
  conversationId,
  minimized,
  conversation,
  me,
}: {
  conversationId: string;
  minimized: boolean;
  conversation: ConversationResponse | undefined;
  me: string;
}) {
  const minimizePane = useMessagingDock((s) => s.minimizePane);
  const restorePane = useMessagingDock((s) => s.restorePane);
  const closePane = useMessagingDock((s) => s.closePane);
  const restoreButtonRef = useRef<HTMLButtonElement>(null);
  const minimizeButtonRef = useRef<HTMLButtonElement>(null);
  const priorMinimizedRef = useRef(minimized);
  const focusAfterOwnTransitionRef = useRef<"restore" | "minimize" | null>(
    null,
  );

  useEffect(() => {
    if (priorMinimizedRef.current === minimized) return;
    priorMinimizedRef.current = minimized;
    const expected = minimized ? "restore" : "minimize";
    if (focusAfterOwnTransitionRef.current === expected) {
      const target = minimized ? restoreButtonRef : minimizeButtonRef;
      target.current?.focus();
    }
    focusAfterOwnTransitionRef.current = null;
  }, [minimized]);

  // A minimized header is not an active message consumer. Passing no id tears
  // down its authority interval/requests while preserving the keyed cache for
  // an eventual restore.
  const messagesQ = useMessages(conversationId, {
    history: false,
    active: !minimized,
  });
  const { scrollContainerRef, onMessageSent } = useScrollToConfirmedMessage(
    messagesQ.messages.map((message) => message.message_id),
  );
  const markRead = useObservedReadBoundary(
    conversationId,
    messagesQ.readMessageBatches,
    !minimized,
    messagesQ.refreshReadAuthorityBatch,
    messagesQ.revokeAuthority,
  );
  const cp = counterpartOf(conversation, me);
  // A cold pane (not in the inbox cache) has no resolvable counterpart → the
  // neutral region name, never the conversation_id uuid.
  const regionName = cp.label === MISSING ? "Conversation" : cp.label;

  if (minimized) {
    return (
      <Paper
        variant="outlined"
        sx={{
          width: 320,
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
          boxShadow: 6,
          pointerEvents: "auto",
        }}
      >
        <Stack direction="row" sx={{ alignItems: "center" }}>
          <Button
            ref={restoreButtonRef}
            type="button"
            onClick={() => {
              focusAfterOwnTransitionRef.current = "minimize";
              restorePane(conversationId);
            }}
            title="Restore conversation"
            color="inherit"
            fullWidth
            sx={{ justifyContent: "flex-start", fontWeight: 700 }}
          >
            {cp.label}
          </Button>
          <IconButton
            type="button"
            onClick={() => closePane(conversationId)}
            aria-label={`Close conversation with ${regionName}`}
          >
            <CloseRoundedIcon />
          </IconButton>
        </Stack>
      </Paper>
    );
  }

  return (
    <Paper
      component="section"
      variant="outlined"
      role="region"
      aria-label={regionName}
      sx={{
        width: 320,
        height: 460,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        boxShadow: 8,
        pointerEvents: "auto",
      }}
    >
      <Stack
        direction="row"
        sx={{
          alignItems: "center",
          px: 1.5,
          py: 0.75,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Typography sx={{ flex: 1, fontWeight: 800 }} noWrap>
          {cp.label}
        </Typography>
        <IconButton
          ref={minimizeButtonRef}
          type="button"
          onClick={() => {
            focusAfterOwnTransitionRef.current = "restore";
            minimizePane(conversationId);
          }}
          aria-label={`Minimize conversation with ${regionName}`}
        >
          <MinimizeRoundedIcon />
        </IconButton>
        <IconButton
          type="button"
          onClick={() => closePane(conversationId)}
          aria-label={`Close conversation with ${regionName}`}
        >
          <CloseRoundedIcon />
        </IconButton>
      </Stack>

      <DockThread
        messagesQ={messagesQ}
        conversationId={conversationId}
        scrollContainerRef={scrollContainerRef}
      />
      {markRead.isError ? (
        <Alert
          severity="warning"
          role="alert"
          action={
            <Button size="small" color="inherit" onClick={markRead.retry}>
              Retry
            </Button>
          }
          sx={{ mx: 1, mb: 1 }}
        >
          Read status not updated.
        </Alert>
      ) : null}
      <MessageComposer
        conversationId={conversationId}
        disabled={!me}
        writeAccess={conversation?.write_access}
        compact
        onMessageSent={onMessageSent}
      />
    </Paper>
  );
}

/**
 * Submit each distinct rendered boundary while a pane is open (non-minimized).
 * The boundary signature prevents an unchanged poll or re-render from repeating
 * the write; a newly rendered message or a later restore can submit new truth.
 * This mirrors the full-thread boundary contract.
 */
function DockThread({
  messagesQ,
  conversationId,
  scrollContainerRef,
}: {
  messagesQ: ReturnType<typeof useMessages>;
  conversationId: string;
  scrollContainerRef: RefObject<HTMLOListElement | null>;
}) {
  const items: MessageResponse[] = messagesQ.messages;
  // The server page is newest-first; reverse for chronological display.
  const ordered = useMemo(() => [...items].reverse(), [items]);

  if (messagesQ.isError && !messagesQ.data) {
    return (
      <Alert severity="error" role="alert" sx={{ m: 1 }}>
        We couldn&apos;t load this conversation. Please try again.
      </Alert>
    );
  }
  if (messagesQ.isLoading) {
    return (
      <Typography color="text.secondary" role="status" sx={{ p: 2 }}>
        Loading messages…
      </Typography>
    );
  }
  if (ordered.length === 0) {
    return (
      <Stack sx={{ flex: 1, alignItems: "center", justifyContent: "center" }}>
        <Typography color="text.secondary">
          No messages yet. Say hello.
        </Typography>
      </Stack>
    );
  }
  return (
    <Box
      component="ol"
      ref={scrollContainerRef}
      role="log"
      aria-live="polite"
      aria-relevant="additions"
      sx={{ flex: 1, overflow: "auto", listStyle: "none", m: 0, p: 1.5 }}
    >
      {ordered.map((message) => (
        <DockBubble
          key={message.message_id}
          message={message}
          conversationId={conversationId}
        />
      ))}
    </Box>
  );
}

/**
 * One compact chat bubble. `data-own` is the server's viewer-relative ownership
 * projection. The typed note is React-escaped and passed through the same safe
 * `linkifyContent` the full thread uses (http/https → real <a>; javascript:/data:
 * stays inert text; no dangerouslySetInnerHTML) — rendered only when non-empty (an
 * empty-with-shared-post message shows just the embed). A shared post rides as a
 * first-class `SharedPostEmbed` BELOW the note (a message may carry both a note AND
 * an embed). An other-sender bubble labels the sender by display_name (em-dash when
 * absent — never the uid).
 */
function DockBubble({
  message,
  conversationId,
}: {
  message: MessageResponse;
  conversationId: string;
}) {
  const isOwn = message.is_own;
  const time = messageTimeLabel(message.created_at);
  const name = isOwn ? "You" : authorLabel(message.sender_display_name);

  return (
    <Box
      component="li"
      data-own={isOwn ? "true" : "false"}
      sx={{
        maxWidth: "100%",
        mb: 0.25,
        px: 1,
        py: 0.75,
        borderRadius: 1,
        "&:hover": { bgcolor: "action.hover" },
        overflowWrap: "anywhere",
      }}
    >
      <Stack spacing={0.5}>
        <Stack direction="row" spacing={0.75} sx={{ alignItems: "baseline" }}>
          <Typography variant="caption" sx={{ fontWeight: 800 }}>
          {name}
          </Typography>
          {time ? <Typography variant="caption" color="text.secondary">{time}</Typography> : null}
        </Stack>
        {message.is_deleted ? (
          <Typography component="span" variant="body2" sx={{ fontStyle: "italic", opacity: 0.8 }}>
            Message deleted
          </Typography>
        ) : message.content ? (
          <Typography component="span" variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
            {linkifyContent(message.content)}
          </Typography>
        ) : null}
        {!message.is_deleted ? (
          <MessageAttachments attachments={message.attachments} compact />
        ) : null}
        {!message.is_deleted ? <SharedPostEmbed message={message} /> : null}
        {isOwn && !message.is_deleted ? (
          <MessageDeleteAction
            conversationId={conversationId}
            messageId={message.message_id}
          />
        ) : null}
      </Stack>
    </Box>
  );
}

// =============================================================================
// New-message compose — player search → get-or-create → open the pane
// =============================================================================

/** Debounce window (ms) before a typed query re-fires the player search — mirrors
 *  MessagesLayout's ComposeMessageDialog so a per-keystroke fetch race is avoided. */
const COMPOSE_SEARCH_DEBOUNCE_MS = 250;

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

/**
 * The dock's new-message search panel: a debounced player search (usePlayerSearch,
 * uid-safe) rendering the server's PlayerCard[] VERBATIM. Selecting a player
 * get-or-creates the 1:1 conversation (useCreateConversation) and OPENS its pane by
 * the RETURNED conversation_id — never the selected player's user_id. A stray
 * self-row is filtered so createConversation([me]) can never fire; a create
 * rejection surfaces honestly and opens no pane.
 */
function DockCompose({
  me,
  onPicked,
}: {
  me: string;
  onPicked: (conversationId: string) => void;
}) {
  const [query, setQuery] = useState("");
  const debounced = useDebouncedValue(query, COMPOSE_SEARCH_DEBOUNCE_MS);
  const searchQ = usePlayerSearch(debounced, "friends");
  const createConversation = useCreateConversation();

  // Self-exclusion (defense in depth — the server already scopes self out).
  const results: PlayerCardData[] = (searchQ.data ?? []).filter(
    (p) => p.user_id !== me,
  );
  const searchActive = debounced.trim().length >= 2;

  function handleSelect(userId: string) {
    if (userId === me || createConversation.isPending) return;
    createConversation.mutate(userId, {
      onSuccess: (conversation) => {
        const id = conversation.conversation_id;
        // Guard a malformed id (a backend contract regression): open ONLY on a
        // non-empty trimmed conversation_id — never the selected player's uid.
        if (typeof id === "string" && id.trim()) onPicked(id);
      },
    });
  }

  return (
    <Stack spacing={1.5} sx={{ p: 1.5 }}>
      <Stack
        component="form"
        role="search"
        onSubmit={(e) => e.preventDefault()}
      >
        <TextField
          type="search"
          label="Search friends by name"
          size="small"
          placeholder="Search friends by name"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          fullWidth
        />
      </Stack>

      {!searchActive ? (
        <Typography color="text.secondary">
          Search by name to start a new message.
        </Typography>
      ) : searchQ.isError ? (
        <Alert severity="error" role="alert">
          We couldn&apos;t search right now. Please try again.
        </Alert>
      ) : searchQ.isLoading ? (
        <Typography color="text.secondary" role="status" aria-live="polite">
          Searching…
        </Typography>
      ) : results.length === 0 ? (
        <Typography color="text.secondary">No friends found.</Typography>
      ) : (
        <List disablePadding>
          {results.map((player) => {
            const isCreating =
              createConversation.isPending &&
              createConversation.variables === player.user_id;
            return (
              <DockPlayerRow
                key={player.user_id}
                player={player}
                busy={isCreating}
                disabled={createConversation.isPending}
                onSelect={() => handleSelect(player.user_id)}
              />
            );
          })}
        </List>
      )}

      {createConversation.isError ? (
        <GuardianAwareError error={createConversation.error}>
          <Alert severity="error" role="alert">
            We couldn&apos;t start that conversation. Please try again.
          </Alert>
        </GuardianAwareError>
      ) : null}
    </Stack>
  );
}

function DockPlayerRow({
  player,
  busy,
  disabled,
  onSelect,
}: {
  player: PlayerCardData;
  busy: boolean;
  disabled: boolean;
  onSelect: () => void;
}) {
  const name = authorLabel(player.display_name, player.user_id);
  const details = [
    typeof player.rating === "number" ? `Rating ${player.rating}` : null,
    player.rating_division ?? null,
  ]
    .filter(Boolean)
    .join(" · ");
  return (
    <ListItem
      disableGutters
      secondaryAction={
        <Button
          type="button"
          size="small"
          variant="contained"
          onClick={onSelect}
          disabled={disabled}
          aria-busy={busy || undefined}
        >
          {busy ? "Starting…" : "Message"}
        </Button>
      }
    >
      <ListItemAvatar>
        <Avatar src={player.avatar_url ?? undefined} alt="" aria-hidden />
      </ListItemAvatar>
      <ListItemText primary={name} secondary={details || undefined} />
    </ListItem>
  );
}

// =============================================================================
// Shared uid-safe helpers
// =============================================================================

/** The counterpart display info for a conversation: the 1:1 person's name +
 *  avatar, the joined group names, or the em-dash placeholder — NEVER a raw uid
 *  and NEVER my own identity (the user_id !== me filter). */
function counterpartOf(
  conversation: ConversationResponse | undefined,
  me: string,
): { label: string; avatarUrl: string | null; displayName: string | null } {
  const others: ConversationParticipantSummary[] = conversation
    ? conversation.participants.filter((p) => p.user_id !== me)
    : [];
  if (others.length === 0) {
    return { label: MISSING, avatarUrl: null, displayName: null };
  }
  if (others.length === 1) {
    const p = others[0];
    return {
      label: authorLabel(p.display_name, p.user_id),
      avatarUrl: p.avatar_url ?? null,
      displayName: p.display_name ?? null,
    };
  }
  return {
    label: others.map((p) => authorLabel(p.display_name, p.user_id)).join(", "),
    avatarUrl: null,
    displayName: null,
  };
}

/** A small avatar: the server image when present, else an initials chip
 *  (authorInitials — never the uid), else a neutral text-free placeholder. */
function DockAvatar({
  url,
  displayName,
}: {
  url: string | null;
  displayName: string | null;
}) {
  const initials = authorInitials(displayName);
  return (
    <Avatar src={url ?? undefined} alt="" aria-hidden>
      {initials}
    </Avatar>
  );
}
